import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Button, Space, Input, Select, Spin, Tooltip, Pagination } from "antd";
import {
  ExperimentOutlined,
  FileSearchOutlined,
  EditFilled,
  DeleteFilled,
  FileFilled,
  FileTextOutlined,
  HeartOutlined,
  WarningOutlined,
  UserOutlined,
  ThunderboltOutlined,
  LineChartOutlined,
  CalendarOutlined,
  MessageOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useGetChatSummaryListQuery, useGetHealthCareSummaryGraphsQuery, useGetHealthCareSummaryQuery } from "../../utils/apiSlice";
import { useEffectiveUserData } from "../../hooks/useEffectiveUserData";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import "./AiChatSummaryList.scss";
import "./healthSummary.scss";
const { Option } = Select;

/** Normalize markdown hashes; keep newlines for tooltip readability */
function stripMarkdownHeadings(raw: string) {
  return raw.replace(/#{1,6}\s/g, "").replace(/\r\n/g, "\n").trim();
}

/** Split summary into lines; if API sends one block, split before "1. TITLE" style sections */
function splitSummaryIntoLines(raw: string): string[] {
  const t = stripMarkdownHeadings(raw);
  if (!t) return [];
  if (t.includes("\n")) {
    return t.split("\n").map((l) => l.trim()).filter(Boolean);
  }
  const parts = t.split(/(?=\s*\d+\.\s+[A-Za-z0-9])/);
  if (parts.length > 1) {
    return parts.map((p) => p.trim()).filter(Boolean);
  }
  return [t];
}

/** Render **markdown bold** as <strong> */
function renderFormattedText(line: string): React.ReactNode {
  const parts = line.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, idx) => {
    const m = part.match(/^\*\*([^*]+)\*\*$/);
    if (m) {
      return (
        <strong key={idx} className="ai-summary-tooltip-strong">
          {m[1]}
        </strong>
      );
    }
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
}

/** Bullet: • - or single * (not **) */
const BULLET_PREFIX = /^(\u2022|[•\-●]|\*(?!\*))\s+/;

/** Rich summary: card header + scroll body (fixes overlap / weird bars from scrolling full panel) */
function AiSummaryTooltipBody({ text }: { text: string }) {
  const lines = splitSummaryIntoLines(text);
  return (
    <div className="ai-summary-tooltip-card">
      <div className="ai-summary-tooltip-card__header">AI Solution Summary</div>
      <div className="ai-summary-tooltip-card__body">
        <div className="ai-summary-tooltip-body ai-summary-tooltip-body--in-card">
          {lines.map((line, i) => {
            const sectionMatch = line.match(/^(\d+)\.\s+(.+)$/);
            if (sectionMatch && sectionMatch[2].length <= 140) {
              return (
                <div key={i} className="ai-summary-tooltip-section">
                  <span className="ai-summary-tooltip-section-num">{sectionMatch[1]}.</span>
                  <span className="ai-summary-tooltip-section-title">
                    {renderFormattedText(sectionMatch[2])}
                  </span>
                </div>
              );
            }
            if (BULLET_PREFIX.test(line)) {
              const body = line.replace(BULLET_PREFIX, "");
              return (
                <div key={i} className="ai-summary-tooltip-bullet">
                  {renderFormattedText(body)}
                </div>
              );
            }
            return (
              <p key={i} className="ai-summary-tooltip-p">
                {renderFormattedText(line)}
              </p>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Shorter text blocks (e.g. problem statement): preserve line breaks */
function TextBlockTooltipBody({ text }: { text: string }) {
  const t = text.replace(/\r\n/g, "\n").trim();
  const lines = t.split(/\n/).map((l) => l.trim()).filter(Boolean);
  return (
    <div className="ai-summary-tooltip-card ai-summary-tooltip-card--sm">
      <div className="ai-summary-tooltip-card__header">Problem statement</div>
      <div className="ai-summary-tooltip-card__body">
        <div className="ai-summary-tooltip-body ai-summary-tooltip-body--in-card ai-summary-tooltip-body--compact">
          {lines.length <= 1 ? (
            <p className="ai-summary-tooltip-p">{renderFormattedText(t)}</p>
          ) : (
            lines.map((line, i) => (
              <p key={i} className="ai-summary-tooltip-p">
                {renderFormattedText(line)}
              </p>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

const Reports = () => {


  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("chat");
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);




  const weightTrendSeries = [{ name: "Weight", data: [140, 139, 138] }];
  const weightTrendInlineOptions: ApexOptions = {
    chart: {
      type: "area",
      height: 110,
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "inherit",
    },
    stroke: { width: 2.5, curve: "smooth", colors: ["#6366f1"] },
    fill: {
      type: "gradient",
      gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.05 },
    },
    markers: {
      size: 5,
      colors: ["#6366f1"],
      strokeColors: "#fff",
      strokeWidth: 2,
      hover: { size: 6 },
    },
    grid: {
      show: true,
      borderColor: "#e2e8f0",
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { left: 36, right: 12, top: 8, bottom: 0 },
    },
    xaxis: {
      categories: ["Aug", "Sep", "Oct"],
      labels: { style: { colors: "#64748b", fontSize: "11px", fontWeight: 500 } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      min: 135,
      max: 142,
      tickAmount: 4,
      labels: {
        style: { colors: "#64748b", fontSize: "10px" },
        formatter: (val: number) => `${Math.round(val)}`,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    tooltip: {
      theme: "light",
      y: { formatter: (val: number) => `${val} lbs` },
      style: { fontSize: "12px" },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
  };

  const userData = useEffectiveUserData();
  const userId = userData?.userId ?? userData?.patientId ?? null;
  const patientIds = userData?.patientId ?? userData?.patientId ?? null;
  const { data: medicationApiData, isLoading: isLoading1 } =
    useGetHealthCareSummaryQuery(
      patientIds, {
      skip: !patientIds || activeTab !== "healthCare",
    }

    );


  const { data, isLoading: isLoading2 } =
    useGetHealthCareSummaryGraphsQuery(
      patientIds, {
      skip: !patientIds || activeTab === "healthCare",
    }

    );
  const apiResponse: any = data?.data || []


  const { data: summaryResponse, isLoading, isError } = useGetChatSummaryListQuery(userId, {
    skip: !userId || activeTab !== "chat"
  });

  /* ---------------- Table Columns ---------------- */

  const labColumns = [
    {
      title: "Date & Time",
      dataIndex: "date",
      width: 140,
      className: "col-date-time",
      render: (text: any) => {
        const str = text != null ? String(text) : "";
        const parts = str.split(" ");
        return (
          <div style={{ fontSize: "12px" }}>
            <div style={{ fontWeight: "600", color: "#262626" }}>{parts[0] || "—"}</div>
            <div style={{ color: "#8c8c8c", fontSize: "11px" }}>{parts.slice(1).join(" ") || "—"}</div>
          </div>
        );
      },
    },
   {
  title: "AI Solution Summary",
  dataIndex: "test",
  width: "35%",
  className: "col-summary",
  render: (text: string) => {
  if (!text) return "—";

    const previewPlain = stripMarkdownHeadings(text).replace(/\s+/g, " ").trim();
    const shortText =
      previewPlain.length > 120 ? `${previewPlain.slice(0, 120)}…` : previewPlain;

    return (
      <Tooltip
        title={<AiSummaryTooltipBody text={text} />}
        placement="topLeft"
        mouseEnterDelay={0.15}
        getPopupContainer={() => document.body}
        overlayClassName="ai-summary-tooltip-overlay"
        overlayInnerStyle={{
          width: 520,
          maxWidth: "min(520px, 92vw)",
          maxHeight: 420,
          padding: 0,
          overflow: "hidden",
          background: "transparent",
          boxShadow: "none",
        }}
        zIndex={10050}
      >
        <span
          className="reports-summary-cell reports-summary-cell-multiline"
          style={{ display: "inline-block", width: "100%", cursor: "pointer" }}
        >
          {shortText}
        </span>
      </Tooltip>
    );
  },
},
    {
      title: "Problem Statement",
      dataIndex: "doctor",
      width: "28%",
      className: "col-problem",
      render: (text: string) => {
        if (!text) return "—";
        return (
          <Tooltip
            title={text.length > 80 ? <TextBlockTooltipBody text={text} /> : undefined}
            placement="topLeft"
            mouseEnterDelay={0.15}
            getPopupContainer={() => document.body}
            overlayClassName="ai-summary-tooltip-overlay ai-summary-tooltip-overlay--sm"
            overlayInnerStyle={{
              maxWidth: "min(400px, 90vw)",
              maxHeight: 280,
              padding: 0,
              overflow: "hidden",
              background: "transparent",
              boxShadow: "none",
            }}
            zIndex={10050}
          >
            <span className="reports-problem-cell">{text}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Chat Logs",
      key: "action",
      width: 120,
      className: "col-actions",
      render: (_: unknown, record: { sessionId?: string | number }) => (
        <Space size={4} className="reports-table-actions">
          <Button
            shape="circle"
            icon={<EditFilled />}
            className="btn-edit"
            // onClick={() => record.sessionId != null && navigate(`/ai-chat-summary-logs/${record.sessionId}`)}
            style={{cursor:"not-allowed"}}
          />
          <Button shape="circle" icon={<DeleteFilled />} className="btn-delete" style={{cursor:"not-allowed"}}/>
          <Button shape="circle" icon={<FileFilled />} className="small-icon" style={{cursor:"not-allowed"}}/>
        </Space>
      ),
    },
  ];

  const formatDateTime = (isoStr: string) => {
    if (!isoStr) return "";
    try {
      const d = new Date(isoStr);
      const date = d.toLocaleDateString("en-CA"); // YYYY-MM-DD
      const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
      return `${date} ${time}`;
    } catch {
      return isoStr;
    }
  };

  const labData = useMemo(() => {
    let raw: any[] = [];
    if (Array.isArray(summaryResponse)) {
      raw = summaryResponse;
    } else if (summaryResponse?.data && Array.isArray(summaryResponse.data)) {
      raw = summaryResponse.data;
    } else if (summaryResponse?.summaries && Array.isArray(summaryResponse.summaries)) {
      raw = summaryResponse.summaries;
    } else if (summaryResponse && typeof summaryResponse === "object" && (summaryResponse.generated_at != null || summaryResponse.summary != null)) {
      raw = [summaryResponse];
    }
    if (!raw.length) return [];
    return raw.map((item: any, index: number) => {
      const dateRaw =
        item.generated_at ??
        item.dateTime ??
        item.date_time ??
        item.last_interaction ??
        item.date ??
        "";
      return {
        key: item.session_id ?? item.sessionId ?? item.id ?? item.key ?? index + 1,
        sessionId: item.session_id ?? item.sessionId ?? item.id ?? item.key ?? String(index + 1),
        date: dateRaw ? formatDateTime(dateRaw) : "",
        test: item.summary ?? item.aiSolutionSummary ?? item.ai_solution_summary ?? item.test ?? "",
        doctor: item.problem_statement ?? item.problemStatement ?? item.doctor ?? "",
        finding: item.finding ?? "",
      };
    });
  }, [summaryResponse]);

  /* ---------------- Health Care Summary ---------------- */

  const imagingColumns = [
    { title: "Patients", dataIndex: "patient" },
    { title: "Imaging Type", dataIndex: "imaging" },
    { title: "Doctor", dataIndex: "doctor" },
    { title: "Date & Time", dataIndex: "date" },
    { title: "Finding", dataIndex: "finding" },
    {
      title: "Action",
      key: "action",
      render: () => (
        <Space size={4} className="reports-table-actions">
          <Button shape="circle" icon={<EditFilled />} className="btn-edit" />
          <Button shape="circle" icon={<FileFilled />} className="small-icon" />
        </Space>
      ),
    },
  ];

  const imagingData: any[] = [
    { key: 1, patient: "Jane Doe", imaging: "MRI Brain", doctor: "Dr. Emily White", date: "2026-03-02 02:00 PM", finding: "Normal" },
    { key: 2, patient: "John Smith", imaging: "Chest X-Ray", doctor: "Dr. Sarah Johnson", date: "2026-03-01 09:30 AM", finding: "Needs Review" },
  ];

  const patientName = userData?.firstName && userData?.lastName
    ? `${userData.firstName} ${userData.lastName}`
    : userData?.firstName || "Patient";
  const patientAge = userData?.age || "—";
  const patientGender = userData?.gender ? (userData.gender === "M" ? "M" : userData.gender === "F" ? "F" : userData.gender) : "—";
  const patientId = userData?.patientId || userData?.userId || "—";

  /* ---------------- Health Summary HTML Content ---------------- */
  const healthSummaryContent = (
    <div className="health-summary-section container-fluid py-4 px-md-5">
      <header className="row align-items-center p-4 mb-4 text-center text-sm-start">
        {/* <div className="col-sm-6 mb-2 mb-sm-0">
          <h1 className="h3 fw-bold m-0 text-uppercase" style={{ letterSpacing: "0.05em" }}>Health Summary</h1>
        </div> */}
        <div className="col-sm-12 text-start">
          <div className="h5 m-0 fw-normal">{patientName} <span className="text-muted">(Age: {patientAge}, {patientGender})</span></div>
          <div className="small text-muted">Patient ID: {patientId}</div>
        </div>
      </header>

      <div className="row g-4">
        <div className="col-lg-4 col-md-6">
          <div className="card shadow-sm custom-card">
            <div className="card-header d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <span className="icon-wrap icon-wrap-danger">
                  <HeartOutlined className="icon-md" style={{ fontSize: "1rem" }} />
                </span>
                <h2 className="h5 m-0 text-uppercase">Vital Signs</h2>
              </div>
            </div>
            <div className="card-body px-3">
              {apiResponse?.vitalResponse && apiResponse.vitalResponse.length > 0 ? (
                <>
                  {/* Heart Rate */}
                  {(() => {
                    const hr = apiResponse.vitalResponse.find((v: any) => v.vitalType === "HR");
                    return (
                      <div className="vital-item mb-2">
                        <div className="d-flex justify-content-between">
                          <span className="label text-muted">Heart Rate</span>
                          <span className="range small text-muted">[90-60]</span>
                        </div>
                        <div className={`h4 m-0 fw-semibold ${hr?.qualityFlag === 'RED_FLAG' ? 'text-danger' : ''}`}>
                          {hr ? `${hr.valueNumeric} ` : '--'}
                          {hr && <span className="h6 text-muted">bpm</span>}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Blood Pressure */}
                  {(() => {
                    const bp = apiResponse.vitalResponse.find((v: any) => v.vitalType === "BP");
                    return (
                      <div className="vital-item mb-2">
                        <div className="d-flex justify-content-between">
                          <span className="label text-muted">Blood Pressure</span>
                          <span className="range small text-muted">[120/80]</span>
                        </div>
                        <div className={`h4 m-0 fw-semibold ${bp?.qualityFlag === 'RED_FLAG' ? 'text-danger' : ''}`}>
                          {bp ? bp.valueText : '--'}
                          {bp && <span className="h6 text-muted"> mmHg</span>}
                        </div>
                      </div>
                    );
                  })()}

                  {/* SpO2 */}
                  {(() => {
                    const spo2 = apiResponse.vitalResponse.find((v: any) => v.vitalType === "SPO2");
                    return (
                      <div className="vital-item mb-2">
                        <div className="d-flex justify-content-between">
                          <span className="label text-muted">SpO2</span>
                          <span className="range small text-muted">[95-100]</span>
                        </div>
                        <div className={`h4 m-0 fw-semibold ${spo2?.qualityFlag === 'RED_FLAG' ? 'text-danger' : ''}`}>
                          {spo2 ? spo2.valueNumeric : '--'}
                          {spo2 && <span className="h6 text-muted">%</span>}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Body Temp */}
                  {(() => {
                    const temp = apiResponse.vitalResponse.find((v: any) => v.vitalType === "TEMP");
                    return (
                      <div className="vital-item">
                        <div className="d-flex justify-content-between">
                          <span className="label text-muted">Body Temp</span>
                          <span className="range small text-muted">[97-99]</span>
                        </div>
                        <div className={`h4 m-0 fw-semibold ${temp?.qualityFlag === 'RED_FLAG' ? 'text-danger' : ''}`}>
                          {temp ? temp.valueNumeric : '--'}
                          {temp && <span className="h6 text-muted"> {temp.unit}</span>}
                        </div>
                      </div>
                    );
                  })()}
                </>
              ) : (
                /* NO DATA STATE */
                <div className="text-center py-5">
                  <div className="text-muted mb-2">
                    <LineChartOutlined style={{ fontSize: '1.5rem', opacity: 0.5 }} />
                  </div>
                  <p className="text-muted small m-0">No vital signs recorded.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-4 col-md-6">
          <div className="card shadow-sm custom-card alert-card">
            <div className="card-header d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <span className="icon-wrap icon-wrap-warning">
                  <WarningOutlined className="icon-md" style={{ fontSize: "1rem" }} />
                </span>
                <h2 className="h5 m-0 text-uppercase">Clinical Alerts</h2>
              </div>
            </div>
            <div className="card-body">
              {apiResponse?.clinicalAlerts && apiResponse.clinicalAlerts.length > 0 ? (
                <ul className="list-unstyled m-0">
                  {/* 1. ALLERGIES */}
                  <li className="alert-item critical mb-3">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <span className="small fw-bold text-danger text-uppercase">Critical</span>
                    </div>
                    <div className="alert-title fw-semibold">Allergies</div>
                    <div className="alert-content text-danger fw-bold">
                      {apiResponse.clinicalAlerts
                        .filter((alert: any) => alert.diagnosisType === "ALLERGY")
                        .map((alert: any) => alert.diagnosisName)
                        .join(", ") || "None"}
                    </div>
                  </li>

                  {/* 2. ACTIVE CONDITIONS */}
                  <li className="alert-item mb-3">
                    <div className="alert-title fw-semibold">Active Conditions</div>
                    <ul className="small text-muted ps-4">
                      {apiResponse.clinicalAlerts.filter((alert: any) => alert.diagnosisType === "DIAGNOSIS").length > 0 ? (
                        apiResponse.clinicalAlerts
                          .filter((alert: any) => alert.diagnosisType === "DIAGNOSIS")
                          .map((alert: any) => (
                            <li key={alert.clinicalAlertId}>{alert.diagnosisName}</li>
                          ))
                      ) : (
                        <li>No active conditions</li>
                      )}
                    </ul>
                  </li>

                  {/* 3. FAMILY HISTORY */}
                  <li className="alert-item">
                    <div className="alert-title fw-semibold">Family History</div>
                    <ul className="small text-muted ps-4">
                      {apiResponse.clinicalAlerts.filter((alert: any) => alert.diagnosisType === "FAMILY_HISTORY").length > 0 ? (
                        apiResponse.clinicalAlerts
                          .filter((alert: any) => alert.diagnosisType === "FAMILY_HISTORY")
                          .map((alert: any) => (
                            <li key={alert.clinicalAlertId}>{alert.diagnosisName}</li>
                          ))
                      ) : (
                        <li>No recorded history</li>
                      )}
                    </ul>
                  </li>
                </ul>
              ) : (
                /* NO DATA STATE */
                <div className="text-center py-4">
                  <div className="text-muted mb-2">
                    <InfoCircleOutlined style={{ fontSize: '1.5rem', opacity: 0.5 }} />
                  </div>
                  <p className="text-muted small m-0">No clinical alerts found for this patient.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-4 col-md-12">
          <div className="card shadow-sm custom-card">
            <div className="card-header d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <span className="icon-wrap icon-wrap-primary"><UserOutlined className="icon-md" style={{ fontSize: "1rem" }} /></span>
                <h2 className="h5 m-0 text-uppercase">Body Metrics</h2>
              </div>
            </div>
            <div className="card-body">
              <div className="row text-center mb-4">
                <div className="col-4">
                  <div className="small text-muted">Height</div>
                  <div className="h5 fw-semibold mb-0">{apiResponse?.heightInCm} cm</div>
                </div>
                <div className="col-4 border-start border-end">
                  <div className="small text-muted">Weight</div>
                  <div className="h5 fw-semibold mb-0">{apiResponse?.weightInKg} <span className="small text-muted">lbs</span></div>
                </div>
                <div className="col-4">
                  <div className="small text-muted">BMI</div>
                  <div className="h5 fw-semibold text-success mb-0">{apiResponse?.bmi}<span className="small text-muted">{apiResponse?.bmiStatus}</span></div>
                </div>
              </div>
              <div className="row align-items-center mt-2">
                <div className="col-12">
                  <h6 className="text-muted m-0 mb-1">Weight trend <span className="small">[3 months]</span></h6>
                  <div className="weight-trend-inline rounded px-2 pt-1">
                    <ReactApexChart options={weightTrendInlineOptions} series={weightTrendSeries} type="area" height={210} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-4 col-md-6">
          <div className="card shadow-sm custom-card">
            <div className="card-header d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <span className="icon-wrap icon-wrap-primary">
                  <ThunderboltOutlined className="icon-md" style={{ fontSize: "1rem" }} />
                </span>
                <h2 className="h5 m-0 text-uppercase">Active Medications</h2>
              </div>
              {/* Optional: Add a badge count for visual flair */}
              <span className="badge bg-primary rounded-pill">
                {apiResponse?.medications?.length || 0}
              </span>
            </div>
            <div className="card-body p-0" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <ul className="list-group list-group-flush">
                {apiResponse?.medications && apiResponse.medications.length > 0 ? (
                  apiResponse.medications.map((med: any) => (
                    <li
                      key={med.patientMedicationId}
                      className="list-group-item d-flex justify-content-between align-items-start px-3 py-2"
                    >
                      <span className="fw-medium">{med.medicationName}</span>
                      <span className="text-muted small">{med.medicineStrength}</span>
                    </li>
                  ))
                ) : (
                  <li className="list-group-item text-center text-muted py-3">
                    No active medications recorded
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>

        <div className="col-lg-4 col-md-6">
          <div className="card shadow-sm custom-card">
            <div className="card-header d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <span className="icon-wrap icon-wrap-success">
                  <LineChartOutlined className="icon-md" style={{ fontSize: "1rem" }} />
                </span>
                <h2 className="h5 m-0 text-uppercase">Recent Lab Results</h2>
              </div>
            </div>
            <div className="card-body p-0" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <ul className="list-group list-group-flush">
                {apiResponse?.labReports && apiResponse.labReports.length > 0 ? (
                  apiResponse.labReports.map((report: any) => {
                    const isAbnormal = report.severity !== "NORMAL";
                    const reportDate = new Date(report.reportDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    });

                    return (
                      <li
                        key={report.labReportId}
                        className="list-group-item d-flex justify-content-between align-items-center px-3 py-2"
                      >
                        <span>
                          {report.reportType}
                          <span className="text-muted small ms-1">({reportDate})</span>
                        </span>
                        <span className={`badge ${isAbnormal
                          ? "bg-danger bg-opacity-10 text-danger"
                          : "bg-success bg-opacity-10 text-success"
                          } rounded-pill px-2 py-1 small`}>
                          {report.severity === "NORMAL" ? "Normal" : `Abnormal - ${report.severity}`}
                        </span>
                      </li>
                    );
                  })
                ) : (
                  <li className="list-group-item text-center text-muted py-3">
                    No lab reports found
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>

        <div className="col-lg-4 col-md-12">
          <div className="card shadow-sm custom-card events-card">
            <div className="card-header d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <span className="icon-wrap icon-wrap-info">
                  <CalendarOutlined className="icon-md" style={{ fontSize: "1rem" }} />
                </span>
                <h2 className="h5 m-0 text-uppercase">Upcoming Events</h2>
              </div>
            </div>
            <div className="card-body px-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {apiResponse?.appointments && apiResponse.appointments.length > 0 ? (
                apiResponse.appointments.map((appt: any) => {
                  const startDate = new Date(appt.scheduledStart);
                  const month = startDate.toLocaleString('en-US', { month: 'short' }).toUpperCase();
                  const day = startDate.getDate();
                  const time = startDate.toLocaleString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  });

                  return (
                    <div key={appt.appointmentId} className="event-item mb-3 d-flex align-items-center gap-3">
                      {/* Dynamic Date Tag */}
                      <div className="date-tag text-white text-center">
                        <div className="day h6 m-0">{month}</div>
                        <div className="date h4 m-0 fw-bold">{day}</div>
                      </div>

                      <div className="event-details flex-grow-1">
                        <div className="event-title fw-semibold text-capitalize">
                          {appt.appointmentType.toLowerCase()} Appointment
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="text-muted small">({time})</div>
                          {/* Status Badge */}
                          <span className={`badge rounded-pill small ${appt.status === 'IN_PROGRESS' ? 'bg-info text-dark' : 'bg-light text-muted'
                            }`}>
                            {appt.status.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center text-muted py-4">
                  No upcoming appointments scheduled.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row mt-4 justify-content-center">
        <div className="col-lg-12">
          <div className="card shadow-sm border-light">
            <div className="card-header bg-white border-bottom-0 pt-3 pb-0 d-flex gap-2 align-items-center justify-content-start">
              <span className="icon-wrap icon-wrap-muted"><MessageOutlined className="icon-md" style={{ fontSize: "1rem" }} /></span>
              <h2 className="h6 m-0 text-muted text-uppercase fw-semibold">Summary of Major Events (from AI Chat)</h2>
            </div>
            <div className="card-body">
              <p className="small text-muted mb-0 lh-base">
                Patient reported persistent dry cough, fatigue, and mild shortness of breath via AI Chat on Oct 18, 2023. Noted symptom duration: 4 days. Expressed concern for flu-like symptoms. Self-monitored fever (100.8°F). Advised immediate follow-up if symptoms worsen.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  /* ---------------- PRESCRIPTIONS ---------------- */



  const [columns, setColumns] = useState(labColumns as any[]);
  const rawTableData = activeTab === "chat" ? labData : imagingData;
  const tableData = useMemo(() => {
    if (!searchText.trim()) return rawTableData;
    const lower = searchText.toLowerCase();
    return rawTableData.filter((row: any) =>
      Object.values(row).some(
        (val) => val && typeof val === "string" && val.toLowerCase().includes(lower)
      )
    );
  }, [rawTableData, searchText]);
  const paginatedTableData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return tableData.slice(start, start + pageSize);
  }, [tableData, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, activeTab]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(tableData.length / pageSize));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [tableData.length, pageSize, currentPage]);
  const tabs = [
    { id: "chat", label: "Ai Chat Summary", icon: <ExperimentOutlined /> },
    { id: "healthCare", label: "Health Care Summary", icon: <FileSearchOutlined /> },

  ];
  const handleTabChange = (tabId: any) => {
    setActiveTab(tabId);
    if (tabId === "chat") setColumns(labColumns);
    if (tabId === "healthCare") setColumns(imagingColumns);
  };
  return (
    <div className="content-wrapper">
      <div className="container-full table-padding">
        {/* Page Header */}
        <div className="page-header">
          <div className="breadcrumb">
            <h3 className="page-title">My Tara Reports</h3>
            <span className="customize-breadcrumb">
              <span className="separator"> - </span>
              <span className="current">My Tara Reports</span>
            </span>
          </div>
        </div>

        {/* Card */}
        <div className="family-card" style={{ paddingTop: '0px' }}>
          {/* Tabs */}
          <div
            style={{
              display: "flex",
              gap: "12px",
              padding: "7px 20px",
              background: "#f0f2f5",
              borderBottom: "1px solid #e5e7eb",
              borderRadius: '12px 12px 0 0'
            }}
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`custom-tab ${activeTab === tab.id ? "active" : ""}`}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* Header - only for AI Chat Summary tab */}
          {activeTab === "chat" && (
            <div className="family-header" style={{ padding: "15px", marginBottom: "0" }}>
              <div className="left-side">
                <p className="title">My Tara Reports</p>
                <Input.Search placeholder="Search..." style={{ width: 250 }} value={searchText} onChange={(e) => setSearchText(e.target.value)} allowClear />
                <Select placeholder="Month" style={{ width: 150 }}>
                  <Option value="3">March</Option>
                </Select>
                <Select placeholder="Year" style={{ width: 120 }}>
                  <Option value="2026">2026</Option>
                </Select>
              </div>
            </div>
          )}

          {/* AI Chat Summary: Table */}
          {activeTab === "chat" && isLoading && (
            <div style={{ padding: "40px", textAlign: "center" }}>
              <Spin tip="Loading summaries..." />
            </div>
          )}
          {activeTab === "chat" && isError && (
            <div style={{ padding: "40px", textAlign: "center", color: "#ff4d4f" }}>
              Failed to load summaries.
            </div>
          )}
          {activeTab === "chat" && !(isLoading || isError) && (
            <>
              <Table
                className="reports-list-table"
                columns={columns}
                dataSource={paginatedTableData}
                pagination={false}
                rowClassName={(_, index) =>
                  index % 2 === 0 ? "table-row-light" : ""
                }
                locale={{
                  emptyText: (
                    <div className="reports-empty-state">
                      <FileTextOutlined className="reports-empty-icon" />
                      <span className="reports-empty-text">No data</span>
                    </div>
                  ),
                }}
                components={{
                  header: {
                    cell: (props: any) => (
                      <th {...props} className={props.className} />
                    ),
                  },
                }}
              />
              <div className="family-footer">
                <span>Total {tableData.length} Reports</span>
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={tableData.length}
                  showSizeChanger
                  pageSizeOptions={["5", "10", "20", "50"]}
                  onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                  }}
                  onShowSizeChange={(_, size) => {
                    setCurrentPage(1);
                    setPageSize(size);
                  }}
                />
              </div>
            </>
          )}

          {/* Health Care Summary: Health Summary cards */}
          {activeTab === "healthCare" && healthSummaryContent}
        </div>
      </div>

    </div>
  );
};

export default Reports;